/**
 * Loading states specific to the pull request surfaces — the first list, a search under way, a
 * timeline and a conversation — drawn as bars in the geometry of the content they stand for.
 *
 * The bars share one tone and one pulse, applied on the container so any number of bars costs a
 * single opacity animation rather than one per bar.
 */
import { cn } from "../../lib/utils";

function GhostBar({ className }: { readonly className?: string | undefined }) {
  return <div aria-hidden className={cn("h-3 rounded bg-muted-foreground/15", className)} />;
}

/** Widths cycle rather than randomize, so the ghost renders the same on every pass. */
const TITLE_WIDTHS = ["w-3/5", "w-2/5", "w-1/2", "w-2/3", "w-2/5", "w-3/5", "w-1/2"];
const META_WIDTHS = ["w-2/5", "w-1/3", "w-2/5", "w-1/4", "w-1/3", "w-2/5", "w-1/3"];

/** Rows in the list's own grid — glyph, title over meta, time over diffstat. */
export function PullRequestListGhost({ rows = 7 }: { readonly rows?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading Pull Requests"
      className="space-y-0.5 motion-safe:animate-skeleton"
    >
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-3 py-2"
        >
          <GhostBar className="size-4 rounded-full" />
          <div className="min-w-0 space-y-1.5">
            <GhostBar className={cn("h-3.5", TITLE_WIDTHS[index % TITLE_WIDTHS.length])} />
            <GhostBar className={META_WIDTHS[index % META_WIDTHS.length]} />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <GhostBar className="w-12" />
            <GhostBar className="w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The Summary's own shape while it loads: the header, the meta rows, a description and a few
 * checks. Standing in for the layout that is coming keeps the loaded pull request from replacing
 * one shape with another a moment later.
 */
export function PullRequestDetailGhost() {
  return (
    <div role="status" aria-label="Loading Pull Request" className="motion-safe:animate-skeleton">
      <div className="border-b border-border/60 px-4 py-3">
        <GhostBar className="w-48" />
        <GhostBar className="mt-2 h-4 w-3/5" />
        <div className="mt-2 flex items-center gap-2">
          <GhostBar className="w-28" />
          <GhostBar className="w-16" />
          <GhostBar className="w-12" />
        </div>
      </div>
      <div className="space-y-2 px-4 py-3">
        {["w-2/5", "w-1/3", "w-2/5"].map((width) => (
          <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-2" key={width}>
            <GhostBar className="w-16" />
            <GhostBar className={width} />
          </div>
        ))}
      </div>
      <div className="space-y-1.5 px-4 pb-3">
        <GhostBar className="w-4/5" />
        <GhostBar className="w-full" />
        <GhostBar className="w-3/5" />
      </div>
      <div className="space-y-2 px-4 pb-4">
        {["w-1/3", "w-2/5", "w-1/4"].map((width) => (
          <div className="flex items-center gap-2" key={width}>
            <GhostBar className="size-3.5 rounded-full" />
            <GhostBar className={width} />
            <GhostBar className="ml-auto w-12" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** The timeline's own shape: dots on the rail, a line and a date to each. */
export function PullRequestTimelineGhost({ rows = 6 }: { readonly rows?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading Timeline"
      className="px-4 py-5 motion-safe:animate-skeleton"
    >
      <div className="relative ml-2 border-l border-border/70 pl-5">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="relative pb-5">
            <GhostBar className="absolute -left-[1.55rem] top-1 size-2 rounded-full" />
            <GhostBar className={cn("h-3.5", TITLE_WIDTHS[index % TITLE_WIDTHS.length])} />
            <GhostBar className="mt-1.5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
